import {
  types,
  flow,
  getParentOfType,
  applySnapshot,
  destroy,
  detach,
  getSnapshot
} from 'mobx-state-tree';
import moment from 'moment';
import axios from '../../common/utils/axios';
import Traits from './traits-model';
import WorkHistory from './workhistory-model';
import Certificate from './certificate-model';
import Language from './language-model';
import { Employee } from '../employee-model';
import { MomentType } from '../types';
import notificationStore from '../../stores/notificationStore';

const seniorityMap = {
  future_talent: 'Future talent',
  intermediate: 'Intermediate',
  senior: 'Senior'
};

export const EmployeeCv = types
  .model('EmployeeCv', {
    id: types.number,
    name: types.maybeNull(types.string),
    editable_name: types.maybeNull(types.string),
    default: types.optional(types.boolean, false),
    updated_at: types.maybeNull(MomentType),
    title: types.optional(types.string, ''),
    description: types.optional(types.string, ''),
    experiences: types.optional(Traits, () => Traits.create([])),
    skills: types.optional(Traits, () => Traits.create([])),
    work_history: types.optional(WorkHistory, () => WorkHistory.create([])),
    certificates: types.optional(types.array(Certificate), []),
    education: types.optional(types.array(Certificate), []), // TODO: these are identical for now, maybe create separate model
    locale: types.optional(types.enumeration(['en', 'fi', 'sv']), 'en'),
    seniority: types.optional(
      types.enumeration(Object.keys(seniorityMap)),
      'future_talent'
    ),
    languages: types.array(Language),
    state: types.optional(
      types.enumeration(['pending', 'ready', 'error']),
      'pending'
    )
  })
  // .postProcessSnapshot(sn => {
  //   const { id, state, ...others } = sn;
  //   return others;
  // })
  .views(self => ({
    lastUpdate() {
      const difference = moment.duration(
        moment(self.updated_at).diff(moment())
      );
      return difference.humanize(true);
    },
    isReady() {
      return self.state === 'ready';
    },
    get isNewOrEmpty() {
      if (!self.isReady()) return false;
      if (self.id === -1) return true;

      const keys = ['experiences', 'skills', 'work_history', 'certificates'];
      const keyContent = keys.map(key => self[key].toJSON().length);

      // Description can be <p></p> and still be considered "empty"
      return self.description.length < 8 && !keyContent.some(Boolean);
    },
    get seniorityLabel() {
      return seniorityMap[self.seniority];
    }
  }))
  .actions(self => ({
    load: flow(function* load() {
      if (self.id === -1 || self.isReady()) {
        return;
      }
      const employee = getParentOfType(self, Employee);
      try {
        const { data } = yield axios.get(
          `/api/v1/employees/${employee.id}/cvs/${self.id}`
        );
        applySnapshot(self, data);
        self.state = 'ready'; // eslint-disable-line no-param-reassign
      } catch (error) {
        console.error('Failed to fetch cv', error); // eslint-disable-line no-console
        self.state = 'error'; // eslint-disable-line no-param-reassign
      }
    }),
    update(data) {
      Object.keys(data).forEach(key => {
        // eslint-disable-next-line no-prototype-builtins
        if (self.hasOwnProperty(key)) {
          self[key] = data[key]; // eslint-disable-line no-param-reassign
        }
      });
    },
    setName(name) {
      self.editable_name = name; // eslint-disable-line no-param-reassign
    },
    setTitle(title) {
      self.title = title; // eslint-disable-line no-param-reassign
    },
    setDescription(description) {
      self.description = description; // eslint-disable-line no-param-reassign
    },
    setSeniority(value) {
      self.seniority = value; // eslint-disable-line no-param-reassign
    },
    addCertificate(cert) {
      self.certificates.push(cert);
    },
    removeCertificate(cert) {
      destroy(cert);
    },
    reorderCertificates(startIndex, endIndex) {
      self.certificates.splice(
        endIndex,
        0,
        detach(self.certificates[startIndex])
      );
    },
    addEducation(cert) {
      self.education.push(cert);
    },
    removeEducation(cert) {
      destroy(cert);
    },
    reorderEducation(startIndex, endIndex) {
      self.education.splice(endIndex, 0, detach(self.education[startIndex]));
    },
    addLanguage(lang) {
      self.languages.push(lang);
    },
    removeLanguage(lang) {
      destroy(lang);
    },
    reorderLanguages(startIndex, endIndex) {
      self.languages.splice(endIndex, 0, detach(self.languages[startIndex]));
    },
    save: flow(function* save() {
      try {
        const employee = getParentOfType(self, Employee);
        const { locale, seniority, editable_name, ...data } = getSnapshot(self);
        const params = { data, locale, seniority, name: editable_name };

        // TODO: make more concise
        if (self.id === -1) {
          const { data: d } = yield axios.post(
            `/api/v1/employees/${employee.id}/cvs`,
            params
          );
          applySnapshot(self, { ...d, state: 'ready' });
          self.changeAllNames(data.name, editable_name);
          return self;
        }

        const {
          data: { updated_at }
        } = yield axios.put(
          `/api/v1/employees/${employee.id}/cvs/${self.id}`,
          params
        );
        applySnapshot(self, { ...self, updated_at, state: 'ready' });
        self.changeAllNames(data.name, editable_name);

        return self;
      } catch ({ response }) {
        return notificationStore.queue(response.data);
      }
    }),
    changeAllNames(prev, next) {
      const employee = getParentOfType(self, Employee);

      if (employee) {
        [employee.cv, ...employee.cvs].forEach(cv => {
          if (cv.name === prev) {
            cv.changeName(next);
          }
        });
      }
    },
    changeName(name) {
      // TODO: this is clunky and risky..
      self.name = name; // eslint-disable-line no-param-reassign
      self.setName(name);
    },
    translate: flow(function* translate() {
      const { id } = getParentOfType(self, Employee);
      return yield axios.post(
        `/api/v1/employees/${id}/cvs/${self.id}/translate`
      );
    }),
    copyDataFrom(cv) {
      const { id, name, locale, editable_name, updated_at, ...data } = cv; // Exclude values
      const copy = JSON.parse(JSON.stringify(data)); // Deep copy the remaining properties

      try {
        applySnapshot(self, { ...self, ...copy, default: false });
      } catch (error) {
        console.error('Error updating CV:', error); // eslint-disable-line no-console
      }
    }
  }));

export default EmployeeCv;
